<script setup>
// Desempenho de vendas (Fase 7, tela 08, spec §4.10): KPIs do funil, distribuição de desfechos por cenário
// (barra empilhada) e últimos leads — dados do endpoint sales_stats, no mesmo período do seletor da página.
// Não substitui os cards existentes: é uma seção a mais, alimentada por conversas com desfecho do Playbook.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import PolarisAssistant from 'dashboard/api/polaris/assistant';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import { Skeleton } from 'dashboard/components-next/polaris/ui/skeleton';

const props = defineProps({
  range: { type: String, required: true },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const stats = ref(null);
const isLoading = ref(false);

const fetchStats = async () => {
  isLoading.value = true;
  try {
    const { data } = await PolarisAssistant.getSalesStats({
      assistantId: route.params.assistantId,
      range: props.range,
    });
    stats.value = data;
  } catch {
    stats.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(() => [props.range, route.params.assistantId], fetchStats, {
  immediate: true,
});

// Uma cor por desfecho, estável pela chave (o mesmo desfecho tem a mesma cor em todas as linhas).
const OUTCOME_COLORS = [
  'bg-n-teal-9',
  'bg-n-blue-9',
  'bg-n-ruby-9',
  'bg-n-amber-9',
  'bg-n-violet-9',
];
const outcomeColor = key => {
  const keys = [
    ...new Set((stats.value?.outcome_mix || []).map(entry => entry.key)),
  ];
  const index = keys.indexOf(key);
  return OUTCOME_COLORS[index % OUTCOME_COLORS.length] || 'bg-n-slate-9';
};

const kpis = computed(() => stats.value?.kpis || {});
const scenarioMix = computed(() => stats.value?.scenario_mix || []);
const latestLeads = computed(() => stats.value?.latest_leads || []);
const outcomeLegend = computed(() => stats.value?.outcome_mix || []);

const scenarioTotal = row =>
  row.outcomes.reduce((sum, entry) => sum + entry.count, 0);

const openConversation = lead => {
  router.push({
    path: `/app/accounts/${route.params.accountId}/conversations/${lead.conversation_display_id}`,
  });
};

const kpiCards = computed(() => [
  {
    key: 'handled',
    label: t('POLARIS.SALES_STATS.HANDLED'),
    value: kpis.value.conversations_handled ?? 0,
  },
  {
    key: 'qualified',
    label: t('POLARIS.SALES_STATS.QUALIFIED'),
    value: kpis.value.qualified_leads ?? 0,
  },
  {
    key: 'withOutcome',
    label: t('POLARIS.SALES_STATS.WITH_OUTCOME'),
    value: kpis.value.conversations_with_outcome ?? 0,
  },
  {
    key: 'handoff',
    label: t('POLARIS.SALES_STATS.HANDOFF_RATE'),
    value: `${kpis.value.handoff_rate ?? 0}%`,
  },
]);
</script>

<template>
  <section class="flex flex-col gap-4" data-testid="sales-performance">
    <div>
      <h2 class="text-sm font-medium text-n-slate-12">
        {{ t('POLARIS.SALES_STATS.HEADER') }}
        <Badge variant="info" class="ml-1">
          {{ t('POLARIS.SALES_STATS.NEW') }}
        </Badge>
      </h2>
      <p class="text-xs text-n-slate-11">
        {{ t('POLARIS.SALES_STATS.SUBHEADER') }}
      </p>
    </div>

    <div
      v-if="isLoading && !stats"
      class="grid grid-cols-2 gap-4 lg:grid-cols-4"
    >
      <Skeleton v-for="i in 4" :key="i" class="h-20 rounded-lg" />
    </div>

    <template v-else>
      <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div
          v-for="card in kpiCards"
          :key="card.key"
          class="rounded-lg border border-n-weak p-4"
        >
          <p class="text-xs text-n-slate-11">{{ card.label }}</p>
          <p class="mt-1 text-2xl font-medium text-n-slate-12">
            {{ card.value }}
          </p>
        </div>
      </div>

      <div class="rounded-lg border border-n-weak p-4">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('POLARIS.SALES_STATS.BY_SCENARIO') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('POLARIS.SALES_STATS.BY_SCENARIO_SUB') }}
        </p>

        <div
          v-if="outcomeLegend.length"
          class="mt-2 flex flex-wrap gap-3 text-xs text-n-slate-11"
        >
          <span
            v-for="entry in outcomeLegend"
            :key="entry.key"
            class="flex items-center gap-1.5"
          >
            <span
              class="size-2.5 rounded-sm"
              :class="outcomeColor(entry.key)"
            />
            {{ entry.title }} ({{ entry.count }})
          </span>
        </div>

        <p v-if="!scenarioMix.length" class="mt-3 text-xs text-n-slate-11">
          {{ t('POLARIS.SALES_STATS.BY_SCENARIO_EMPTY') }}
        </p>

        <div v-else class="mt-3 flex flex-col gap-3">
          <div
            v-for="row in scenarioMix"
            :key="row.scenario_id"
            class="grid grid-cols-[minmax(0,12rem)_4rem_1fr] items-center gap-3"
          >
            <span class="truncate text-sm text-n-slate-12">{{
              row.title
            }}</span>
            <span class="text-right text-xs text-n-slate-11">{{
              row.conversations
            }}</span>
            <div
              class="flex h-2.5 overflow-hidden rounded-full bg-n-alpha-black2"
            >
              <span
                v-for="entry in row.outcomes"
                :key="entry.key"
                class="h-full"
                :class="outcomeColor(entry.key)"
                :style="{
                  width: `${(entry.count / scenarioTotal(row)) * 100}%`,
                }"
                :title="`${entry.title}: ${entry.count}`"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="rounded-lg border border-n-weak p-4">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('POLARIS.SALES_STATS.LATEST_LEADS') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('POLARIS.SALES_STATS.LATEST_LEADS_SUB') }}
        </p>

        <p v-if="!latestLeads.length" class="mt-3 text-xs text-n-slate-11">
          {{ t('POLARIS.SALES_STATS.LATEST_LEADS_EMPTY') }}
        </p>

        <table v-else class="mt-3 w-full text-sm">
          <thead>
            <tr class="text-left text-xs text-n-slate-11">
              <th class="pb-2 font-normal">
                {{ t('POLARIS.SALES_STATS.LEAD') }}
              </th>
              <th class="pb-2 font-normal">
                {{ t('POLARIS.SALES_STATS.COMPANY') }}
              </th>
              <th class="pb-2 font-normal">
                {{ t('POLARIS.SALES_STATS.OUTCOME') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="lead in latestLeads"
              :key="lead.conversation_id"
              class="cursor-pointer border-t border-n-weak hover:bg-n-alpha-black2"
              @click="openConversation(lead)"
            >
              <td class="py-2 text-n-brand">{{ lead.name || '—' }}</td>
              <td class="py-2 text-n-slate-12">{{ lead.company || '—' }}</td>
              <td class="py-2">
                <Badge :variant="lead.qualified ? 'success' : 'outline'">
                  {{ lead.outcome_title }}
                </Badge>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </section>
</template>
